import { useEffect } from 'react';
import { Environment, explicitLogoutFunction } from '@pcid/web-sdk';
import { getLastViewedPage, getTokens, removeTokens } from 'Utils/auth';
import { Spinner } from 'Shared/spinner';
import * as Sentry from '@sentry/react';
import { SSO } from 'Data/constants';
import { useRouter } from 'next/router';
import { ROUTES } from '@/utils';

export default function LogoutRedirect() {
  const router = useRouter();

  const logout = async () => {
    try {
      // Delete session from Redis (if enabled) - fire and forget
      if (process.env.NEXT_PUBLIC_SESSION_MANAGEMENT_ENABLED === 'true') {
        fetch('/api/pcid/session', {
          method: 'DELETE',
          credentials: 'include',
          keepalive: true,
        }).catch((err) => console.warn('Session delete failed:', err));
      }

      const { id_token, refresh_token } = getTokens();
      const request = {
        idToken: id_token || '',
        refreshToken: refresh_token || '',
        redirectUri: `${window.location.origin}/${SSO.LOGOUT}`,
        kmsiLogout: true,
        clientId: process.env.NEXT_PUBLIC_PCID_CLIENT_ID || '',
        clientSecret: process.env.NEXT_PUBLIC_PCID_CLIENT_SECRET || '',
      };
      const environment = process.env.NEXT_PUBLIC_APP_ENV === 'prod' ? Environment.PRODUCTION : Environment.LOWER;
      const logoutPath = explicitLogoutFunction(environment, request);
      window.location.replace(logoutPath);
    } catch (err) {
      /*
        Preventing the user has an endless loop experience even though we have an error.
      */
      Sentry.captureMessage('Log out error: ' + err);
      removeTokens();
      await router.push(ROUTES.HOME_PAGE);
    }
  };

  const loadToken = async (token: string) => {
    try {
      removeTokens();
      const base64 = token.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );
      const obj = JSON.parse(jsonPayload);
      const { status } = obj;
      const { USER_LOGOUT } = status;
      if (USER_LOGOUT === SSO.SUCCESS) {
        const lastViewedPage = getLastViewedPage();
        Sentry.captureMessage('SSO log out Successfully ');
        await router.push(lastViewedPage);
      } else {
        Sentry.captureMessage('Log out error: ' + JSON.stringify(obj));
        await router.push(ROUTES.HOME_PAGE);
      }
    } catch (err) {
      Sentry.captureMessage('Log out error: ' + err);
      removeTokens();
      await router.push(ROUTES.HOME_PAGE);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const response = urlParams.get(SSO.RESPONSE) || '';
    if (response === '') {
      logout();
    } else {
      loadToken(response);
    }
  }, []);

  return <Spinner />;
}
